/**
 * PillarDetailModal — Iter. 406.fr.4
 *
 * Tap any insight card in PatternInsights -> this modal opens and calls
 * GET /insights/pillar-detail/{pillar_id}?period={period}. Renders a
 * pillar-specific deep dive (timeline sparkline + macro split / hourly
 * distribution / quality split / top items) plus the AI commentary
 * generated server-side.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  X, Loader2, TrendingUp, Award, AlertCircle, Sparkles, BarChart3,
} from 'lucide-react';
import { api } from '../context/AppContext';

const PILLAR_CHROME = {
  vitalite_mouvement: { accent: 'rose-400', bg: 'from-rose-500/15 to-rose-700/5', border: 'border-rose-400/40', icon: '💪' },
  nutrition_carburant: { accent: 'lime-400', bg: 'from-lime-500/15 to-emerald-700/5', border: 'border-lime-400/40', icon: '🥩' },
  recuperation_optimale: { accent: 'sky-400', bg: 'from-sky-500/15 to-cyan-700/5', border: 'border-sky-400/40', icon: '💧' },
  force_mentale: { accent: 'fuchsia-400', bg: 'from-fuchsia-500/15 to-purple-700/5', border: 'border-fuchsia-400/40', icon: '🧠' },
  sommeil_equilibre: { accent: 'indigo-400', bg: 'from-indigo-500/15 to-violet-700/5', border: 'border-indigo-400/40', icon: '💤' },
  performance_globale: { accent: 'amber-400', bg: 'from-amber-500/15 to-orange-700/5', border: 'border-amber-400/40', icon: '🎯' },
};

const PERIOD_LABEL_FR = { '1d': 'Jour', '1w': '1 semaine', '2w': '2 semaines', '1m': '1 mois', '3m': '3 mois', '6m': '6 mois', '1y': '1 an' };

export default function PillarDetailModal({ pillarId, period = '1m', language = 'fr', onClose }) {
  const fr = String(language || 'fr').startsWith('fr');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const chrome = PILLAR_CHROME[pillarId] || PILLAR_CHROME.vitalite_mouvement;
  // Iter. 406.fr.7 — Scroll-top reset ref. The modal opens as a bottom-sheet
  // on mobile (`items-end`) and the inner scroll container's scrollTop must
  // be forced to 0 whenever (a) the modal mounts, (b) the data arrives after
  // the AI commentary loads. Otherwise the user lands on a mid/late scroll
  // position and sees only the title bar with empty space below.
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Reset scroll-to-top BEFORE the network call so the header is always
    // visible at open time. Also runs again when data settles (see next
    // useEffect) to catch the height-jump that happens when the AI
    // commentary block grows the modal.
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    api.get(`/insights/pillar-detail/${pillarId}?period=${period}`)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => {
        if (cancelled) return;
        const msg = err?.response?.data?.detail || (fr ? 'Erreur de chargement' : 'Loading error');
        setError(typeof msg === 'string' ? msg : (fr ? 'Erreur de chargement' : 'Loading error'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pillarId, period, fr]);

  // Iter. 406.fr.7 — second reset once data/loading settle so the modal lands
  // on the title even after the AI block expands the layout.
  // Iter. 406.fr.21 — Strengthened with multiple delayed retries and a
  // ResizeObserver. On some pillars (notably Performance Globale, the last
  // tile) the AI commentary block keeps growing after the first render, which
  // would re-position the scrollTop mid-content and leave the user staring at
  // a black backdrop until they scrolled manually.
  useEffect(() => {
    if (scrollRef.current && (data || error) && !loading) {
      const el = scrollRef.current;
      const reset = () => { if (scrollRef.current) scrollRef.current.scrollTop = 0; };
      // Immediate + 2 rAFs + 80/250/500ms safety net.
      reset();
      requestAnimationFrame(() => {
        reset();
        requestAnimationFrame(reset);
      });
      const t1 = setTimeout(reset, 80);
      const t2 = setTimeout(reset, 250);
      const t3 = setTimeout(reset, 500);
      // Observe content growth (AI commentary streaming, late images, etc.)
      let ro = null;
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => reset());
        ro.observe(el);
      }
      return () => {
        clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
        if (ro) ro.disconnect();
      };
    }
    return undefined;
  }, [data, error, loading]);

  // ESC key closes the modal
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const title = data ? (fr ? data.title_fr : data.title_en) : (fr ? 'Analyse détaillée' : 'Deep dive');

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-hidden"
      onClick={onClose}
      data-testid="pillar-detail-backdrop"
    >
      <div
        ref={scrollRef}
        className={`bg-card border ${chrome.border} rounded-b-3xl sm:rounded-3xl max-w-lg w-full p-5 shadow-2xl max-h-[92vh] overflow-y-auto overscroll-contain flex flex-col items-stretch justify-start`}
        onClick={(e) => e.stopPropagation()}
        data-testid="pillar-detail-modal"
        style={{ scrollBehavior: 'auto' }}
      >
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${chrome.bg} border ${chrome.border} flex items-center justify-center text-3xl shrink-0`} aria-hidden="true">
            {chrome.icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-[10px] uppercase tracking-widest text-${chrome.accent} font-bold flex items-center gap-1.5`}>
              <Sparkles className="w-3 h-3" />
              {fr ? `Analyse · ${PERIOD_LABEL_FR[period] || period}` : `Analysis · ${period}`}
            </p>
            <h2 className="font-heading text-base uppercase tracking-tight text-foreground leading-tight mt-0.5" data-testid="pillar-detail-title">
              {title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1 shrink-0"
            aria-label={fr ? 'Fermer' : 'Close'}
            data-testid="pillar-detail-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-2" data-testid="pillar-detail-loading">
            <Loader2 className={`w-7 h-7 text-${chrome.accent} animate-spin`} />
            <p className="text-xs text-muted-foreground">
              {fr ? 'Compilation de ton analyse...' : 'Compiling your analysis...'}
            </p>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-xl bg-destructive/10 border border-destructive/30 px-4 py-3 text-center" data-testid="pillar-detail-error">
            <AlertCircle className="w-5 h-5 text-destructive/80 mx-auto mb-1" />
            <p className="text-xs text-destructive/90">{error}</p>
          </div>
        )}

        {!loading && data && (
          <div className="space-y-4" data-testid="pillar-detail-body">
            {/* AI commentary */}
            {data.ai_commentary && (
              <div className={`rounded-2xl bg-gradient-to-br ${chrome.bg} border ${chrome.border} px-4 py-3`} data-testid="pillar-detail-ai">
                <p className={`text-[10px] uppercase tracking-wider text-${chrome.accent} font-bold mb-1.5 flex items-center gap-1.5`}>
                  <Sparkles className="w-3 h-3" />
                  {fr ? 'Lecture Coach YUMIX' : 'YUMIX Coach reading'}
                </p>
                <p className="text-[12px] text-foreground/95 leading-relaxed whitespace-pre-line">
                  {data.ai_commentary}
                </p>
              </div>
            )}

            {/* Pillar-specific KPIs */}
            <PillarBody pillarId={pillarId} data={data} fr={fr} accent={chrome.accent} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Pillar-specific deep dive bodies
// ─────────────────────────────────────────────────────────────────────────
function PillarBody({ pillarId, data, fr, accent }) {
  if (pillarId === 'nutrition_carburant') return <NutritionBody data={data} fr={fr} accent={accent} />;
  if (pillarId === 'vitalite_mouvement') return <MovementBody data={data} fr={fr} accent={accent} />;
  if (pillarId === 'recuperation_optimale') return <HydrationBody data={data} fr={fr} accent={accent} />;
  if (pillarId === 'force_mentale') return <MindsetBody data={data} fr={fr} accent={accent} />;
  if (pillarId === 'sommeil_equilibre') return <SleepBody data={data} fr={fr} accent={accent} />;
  if (pillarId === 'performance_globale') return <OverallBody data={data} fr={fr} accent={accent} />;
  return null;
}

function StatGrid({ stats }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="pillar-stat-grid">
      {stats.map((s, i) => (
        <div key={i} className="rounded-xl bg-secondary/40 border border-border/40 px-3 py-2.5">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">{s.label}</p>
          <p className="text-lg font-black text-foreground leading-tight mt-0.5">{s.value}</p>
          {s.sub && <p className="text-[10px] text-muted-foreground/80">{s.sub}</p>}
        </div>
      ))}
    </div>
  );
}

function MiniBarChart({ data, valueKey, label, max, fr }) {
  if (!data || data.length === 0) {
    return <p className="text-[11px] text-muted-foreground italic">{fr ? 'Pas encore de données' : 'No data yet'}</p>;
  }
  const computedMax = max ?? Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div data-testid="pillar-mini-chart">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1.5 flex items-center gap-1">
        <BarChart3 className="w-3 h-3" /> {label}
      </p>
      <div className="flex items-end gap-0.5 h-20 bg-secondary/30 rounded-lg p-1.5 border border-border/30">
        {data.map((d, i) => {
          const h = Math.max(2, Math.round((d[valueKey] || 0) / computedMax * 100));
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end gap-0.5 min-w-0">
              <div className="w-full bg-primary/70 rounded-sm hover:bg-primary transition-colors" style={{ height: `${h}%` }} title={`${d.date}: ${d[valueKey]}`} />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[8.5px] text-muted-foreground/70 mt-1">
        <span>{data[0]?.date?.slice(5) || ''}</span>
        <span>{data[data.length - 1]?.date?.slice(5) || ''}</span>
      </div>
    </div>
  );
}

function NutritionBody({ data, fr }) {
  const a = data.averages_per_day || {};
  const t = data.targets || {};
  const split = data.macro_split_pct || {};
  return (
    <>
      <StatGrid stats={[
        { label: fr ? 'Repas' : 'Meals', value: data.total_meals, sub: `${data.days_with_meals} ${fr ? 'jours' : 'days'}` },
        { label: fr ? 'Protéines/j' : 'Protein/d', value: `${a.protein_g || 0}g`, sub: `${fr ? 'cible' : 'tgt'} ${t.protein_min}-${t.protein_max}g` },
        { label: fr ? 'Calories/j' : 'Calories/d', value: Math.round(a.calories || 0), sub: `${fr ? 'cible' : 'tgt'} ${t.calories}` },
        { label: fr ? 'Glucides/j' : 'Carbs/d', value: `${a.carbs_g || 0}g`, sub: `${split.carbs_pct || 0}%` },
        { label: fr ? 'Lipides/j' : 'Fats/d', value: `${a.fats_g || 0}g`, sub: `${split.fats_pct || 0}%` },
        { label: fr ? 'Fibres/j' : 'Fiber/d', value: `${a.fiber_g || 0}g`, sub: `${fr ? 'cible' : 'tgt'} ${t.fiber}g` },
      ]} />
      <div className="rounded-xl bg-secondary/40 border border-border/40 px-3 py-2.5">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1.5">
          {fr ? 'Répartition macros (% kcal)' : 'Macro split (% kcal)'}
        </p>
        <div className="flex h-3 rounded-full overflow-hidden bg-black/40" data-testid="macro-split-bar">
          <div className="bg-rose-400/80" style={{ width: `${split.protein_pct || 0}%` }} title={`${fr ? 'Protéines' : 'Protein'} ${split.protein_pct}%`} />
          <div className="bg-amber-400/80" style={{ width: `${split.carbs_pct || 0}%` }} title={`${fr ? 'Glucides' : 'Carbs'} ${split.carbs_pct}%`} />
          <div className="bg-cyan-400/80" style={{ width: `${split.fats_pct || 0}%` }} title={`${fr ? 'Lipides' : 'Fats'} ${split.fats_pct}%`} />
        </div>
        <div className="flex justify-between text-[10px] mt-1">
          <span className="text-rose-300">P {split.protein_pct || 0}%</span>
          <span className="text-amber-300">G {split.carbs_pct || 0}%</span>
          <span className="text-cyan-300">L {split.fats_pct || 0}%</span>
        </div>
      </div>
      <MiniBarChart data={data.timeline} valueKey="protein_g" label={fr ? 'Protéines (g) — 30 derniers jours' : 'Protein (g) — last 30 days'} fr={fr} />
      {data.best_protein_day && (
        <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 flex items-center gap-2">
          <Award className="w-4 h-4 text-emerald-300" />
          <p className="text-[11px] text-emerald-100">
            <strong>{fr ? 'Meilleur jour' : 'Best day'}</strong> : {data.best_protein_day.date} — {data.best_protein_day.protein_g}g {fr ? 'protéines' : 'protein'}
          </p>
        </div>
      )}
    </>
  );
}

function MovementBody({ data, fr }) {
  const dist = data.workout_type_distribution || {};
  const types = Object.entries(dist).sort((a, b) => b[1] - a[1]).slice(0, 5);
  return (
    <>
      <StatGrid stats={[
        { label: fr ? 'Séances' : 'Sessions', value: data.total_workouts, sub: `${data.workouts_per_week}/${fr ? 'sem' : 'wk'}` },
        { label: fr ? 'Cible/sem' : 'Target/wk', value: data.target_per_week },
        { label: fr ? 'Minutes' : 'Minutes', value: data.total_minutes, sub: `${data.avg_session_minutes} ${fr ? 'min/séance' : 'min/session'}` },
        { label: fr ? 'kcal brûlées' : 'kcal burned', value: data.total_calories_burned },
        { label: fr ? 'Jours actifs' : 'Active days', value: data.active_days },
        { label: fr ? 'Série max' : 'Best streak', value: `${data.longest_streak_days || 0}j` },
      ]} />
      {types.length > 0 && (
        <div className="rounded-xl bg-secondary/40 border border-border/40 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
            {fr ? 'Types d\'entraînement' : 'Workout types'}
          </p>
          <ul className="space-y-1">
            {types.map(([type, count]) => (
              <li key={type} className="flex items-center gap-2 text-[11px]">
                <span className="capitalize text-foreground/85 flex-1 truncate">{type}</span>
                <span className="text-muted-foreground">{count}×</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {data.top_exercises?.length > 0 && (
        <div className="rounded-xl bg-secondary/40 border border-border/40 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
            {fr ? 'Top 5 exercices' : 'Top 5 exercises'}
          </p>
          <ul className="space-y-1">
            {data.top_exercises.map((ex, i) => (
              <li key={i} className="flex items-center gap-2 text-[11px]">
                <span className="text-rose-300 font-bold w-4">{i + 1}.</span>
                <span className="text-foreground/85 flex-1 truncate">{ex.name}</span>
                <span className="text-muted-foreground">{ex.count}×</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function HydrationBody({ data, fr }) {
  return (
    <>
      <StatGrid stats={[
        { label: fr ? 'Logs eau' : 'Water logs', value: data.total_logs, sub: `${data.days_with_water} ${fr ? 'jours' : 'days'}` },
        { label: fr ? 'Moyenne/j' : 'Avg/day', value: `${data.avg_ml_per_day} mL`, sub: `${fr ? 'cible' : 'tgt'} ${data.target_ml_per_day} mL` },
        { label: fr ? 'Cible atteinte' : 'Hit target', value: `${data.on_target_days}j`, sub: `${fr ? 'de' : 'of'} ${data.days_with_water}` },
        { label: fr ? 'Heure pic' : 'Peak hour', value: data.peak_hour != null ? `${data.peak_hour}h` : '—' },
      ]} />
      <MiniBarChart data={data.timeline} valueKey="ml" label={fr ? 'Hydratation (mL) — 30 derniers jours' : 'Hydration (mL) — last 30 days'} fr={fr} />
      {Array.isArray(data.hourly_distribution) && data.hourly_distribution.some(v => v > 0) && (
        <div className="rounded-xl bg-secondary/40 border border-border/40 px-3 py-2.5" data-testid="hydration-hourly">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1.5">
            {fr ? 'Répartition horaire' : 'Hourly distribution'}
          </p>
          <div className="flex items-end gap-0.5 h-12">
            {data.hourly_distribution.map((v, h) => {
              const maxv = Math.max(...data.hourly_distribution, 1);
              const pct = Math.max(2, Math.round(v / maxv * 100));
              return <div key={h} className="flex-1 bg-sky-400/60 hover:bg-sky-400 rounded-sm transition-colors" style={{ height: `${pct}%` }} title={`${h}h: ${Math.round(v)}mL`} />;
            })}
          </div>
          <div className="flex justify-between text-[8.5px] text-muted-foreground/70 mt-1">
            <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
          </div>
        </div>
      )}
    </>
  );
}

function MindsetBody({ data, fr }) {
  const DOW = fr ? ['L', 'M', 'M', 'J', 'V', 'S', 'D'] : ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  return (
    <>
      <StatGrid stats={[
        { label: fr ? 'Prompts' : 'Prompts', value: data.total_logs },
        { label: fr ? 'Interactions' : 'Interactions', value: data.total_interactions },
        { label: fr ? 'Logs/sem' : 'Logs/wk', value: data.logs_per_week },
      ]} />
      {data.top_themes?.length > 0 && (
        <div className="rounded-xl bg-secondary/40 border border-border/40 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
            {fr ? 'Thèmes récurrents' : 'Recurring themes'}
          </p>
          <ul className="space-y-1">
            {data.top_themes.map((t, i) => (
              <li key={i} className="flex items-center gap-2 text-[11px]">
                <span className="text-fuchsia-300 font-bold">·</span>
                <span className="capitalize text-foreground/85 flex-1 truncate">{t.theme}</span>
                <span className="text-muted-foreground">{t.count}×</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(data.by_day_of_week) && data.by_day_of_week.some(v => v > 0) && (
        <div className="rounded-xl bg-secondary/40 border border-border/40 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
            {fr ? 'Activité par jour' : 'Activity by day'}
          </p>
          <div className="flex items-end gap-1 h-14">
            {data.by_day_of_week.map((v, i) => {
              const maxv = Math.max(...data.by_day_of_week, 1);
              const pct = Math.max(4, Math.round(v / maxv * 100));
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full bg-fuchsia-400/60 hover:bg-fuchsia-400 rounded-sm transition-colors" style={{ height: `${pct}%` }} title={`${v}`} />
                  <span className="text-[9px] text-muted-foreground">{DOW[i]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

function SleepBody({ data, fr }) {
  return (
    <>
      <StatGrid stats={[
        { label: fr ? 'Nuits' : 'Nights', value: data.total_nights },
        { label: fr ? 'Durée moy.' : 'Avg duration', value: `${data.avg_duration_hours}h`, sub: `${fr ? 'cible' : 'tgt'} ${data.target_hours}h` },
        { label: 'Score', value: data.avg_score || '—' },
        { label: fr ? 'Cible atteinte' : 'Hit target', value: `${data.nights_on_target}n` },
        { label: fr ? 'Dette' : 'Sleep debt', value: `${data.sleep_debt_hours}h` },
        { label: fr ? 'Cycles' : 'Cycles', value: data.total_cycles },
      ]} />
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-3 py-2">
          <p className="text-[9px] uppercase text-emerald-300 font-bold">{fr ? 'Plus longue' : 'Longest'}</p>
          <p className="text-base font-black text-emerald-100">{data.longest_night_hours}h</p>
        </div>
        <div className="rounded-xl bg-orange-500/10 border border-orange-500/30 px-3 py-2">
          <p className="text-[9px] uppercase text-orange-300 font-bold">{fr ? 'Plus courte' : 'Shortest'}</p>
          <p className="text-base font-black text-orange-100">{data.shortest_night_hours}h</p>
        </div>
      </div>
      <MiniBarChart data={data.timeline} valueKey="duration_hours" label={fr ? 'Durée (h) — 30 dernières nuits' : 'Duration (h) — last 30 nights'} fr={fr} />
      {data.top_factors?.length > 0 && (
        <div className="rounded-xl bg-secondary/40 border border-border/40 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
            {fr ? 'Facteurs récurrents' : 'Recurring factors'}
          </p>
          <ul className="space-y-1">
            {data.top_factors.map(([k, c]) => (
              <li key={k} className="flex items-center gap-2 text-[11px]">
                <span className="capitalize text-foreground/85 flex-1 truncate">{String(k).replace(/_/g, ' ')}</span>
                <span className="text-muted-foreground">{c}×</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function OverallBody({ data, fr }) {
  const lc = data.logs_count || {};
  return (
    <>
      <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-center" data-testid="overall-engagement">
        <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold flex items-center justify-center gap-1.5">
          <TrendingUp className="w-3 h-3" /> {fr ? 'Score d\'engagement' : 'Engagement score'}
        </p>
        <p className="text-4xl font-black text-amber-200 leading-none mt-1">{data.engagement_score}%</p>
        <p className="text-[10px] text-amber-300/70 mt-1">
          {data.active_pillars}/5 {fr ? 'piliers actifs' : 'active pillars'}
        </p>
      </div>
      <StatGrid stats={[
        { label: fr ? 'Repas' : 'Meals', value: lc.meals || 0 },
        { label: fr ? 'Séances' : 'Workouts', value: lc.workouts || 0 },
        { label: fr ? 'Logs eau' : 'Water', value: lc.water || 0 },
        { label: fr ? 'Mindset' : 'Mindset', value: lc.mindset || 0 },
        { label: fr ? 'Sommeil' : 'Sleep', value: lc.sleep || 0 },
      ]} />
    </>
  );
}
